import { Button, Tile } from '@carbon/react';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import { Edit } from '@carbon/react/icons';
import { LaunchLink } from 'components/launch-link';
import {
  SCENARIO_DOMAINS,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import { AreaTags } from './area-tags';
import { MigrationSuggestion } from './migration-suggestion';
import type {
  MigrationSuggestion as Suggestion,
  ScenarioChoice,
} from './types';
import styles from './case-editor.module.scss';

export interface DomainTilesProps {
  /** The form's scenario per domain. */
  scenarios: Record<ScenarioDomainKey, ScenarioChoice | null>;
  /**
   * The archived scenario each domain had when the case loaded. A tile is tagged Archived only
   * while it still holds that scenario, not after another is picked.
   */
  archivedScenarioIds: Partial<Record<ScenarioDomainKey, number>>;
  /** Opens the picker for a domain. */
  onEdit: (domain: ScenarioDomainKey) => void;
  /** The migration's suggestion per domain, shown while the domain has no scenario. */
  suggestions?: Partial<Record<ScenarioDomainKey, Suggestion>>;
  /** Picks a scenario for a domain (a suggestion confirmed, or a candidate). */
  onPick?: (domain: ScenarioDomainKey, choice: ScenarioChoice) => void;
}

/**
 * The eight domain tiles, in registry order: Price Deck, Development Pace, Type Curve, DCA,
 * Taxes, Expenses, Differentials, Discount Rates. Each shows its scenario and the areas it
 * covers, or "No scenario assigned", and opens the picker to assign, change or clear it.
 *
 * @param {DomainTilesProps} props - The scenarios, archived domains and edit handler.
 * @return {JSX.Element} The tiles.
 */
export function DomainTiles({
  scenarios,
  archivedScenarioIds,
  onEdit,
  suggestions = {},
  onPick,
}: DomainTilesProps) {
  return (
    <div className={styles.tiles} aria-label="Scenario assignments">
      {SCENARIO_DOMAINS.map((domain) => {
        const scenario = scenarios[domain.key];
        return (
          <Tile
            key={domain.key}
            className={styles.tile}
            data-testid={`domain-tile-${domain.key}`}
          >
            <div className={styles.tileHeader}>
              <span className={styles.tileLabel}>{domain.label}</span>
              <span className={styles.tileActions}>
                {scenario && (
                  <LaunchLink
                    to={`/${domain.scenarioPath}/${scenario.id}`}
                    label={`Open ${domain.label} scenario in a new tab`}
                  />
                )}
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Edit}
                  iconDescription={`Edit ${domain.label}`}
                  onClick={() => onEdit(domain.key)}
                />
              </span>
            </div>
            {scenario ? (
              <>
                <span className={styles.tileScenario}>
                  {scenario.name}
                  {archivedScenarioIds[domain.key] === scenario.id && (
                    <LifecycleStatusTag status="archived" />
                  )}
                </span>
                <AreaTags
                  scenario={scenario}
                  gatesCoverage={domain.gatesCoverage}
                />
              </>
            ) : (
              <>
                <span className={styles.tileEmpty}>No scenario assigned</span>
                {suggestions[domain.key] && onPick && (
                  <MigrationSuggestion
                    suggestion={suggestions[domain.key]!}
                    onPick={(choice) => onPick(domain.key, choice)}
                  />
                )}
              </>
            )}
          </Tile>
        );
      })}
    </div>
  );
}
